import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import MonthHistoryAmountColumn from "./MonthHistoryAmountColumn";

const ClockIcon = () => (
  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const MonthlyHistoryCards = ({ history, formatAmount, onMonthClick, onDeleteMonth, defaultCurrency = "PEN" }) => {
  const navigate = useNavigate();

  const handleClick = (month) => {
    if (onMonthClick) {
      onMonthClick(month);
    } else {
      navigate(`/month/${month.id}`);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <ClockIcon />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Historial de Meses</h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pl-7">
          Importes por moneda real; si hay varias, verás una línea por moneda.
        </p>
      </div>

      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {history.map((month) => (
          <div
            key={month.id}
            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-lg font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer flex-1 min-w-0"
                onClick={() => handleClick(month)}
              >
                {month.name}
              </h3>
              <div className="flex items-center gap-1">
                {onDeleteMonth && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMonth(e, month);
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    aria-label={`Eliminar ${month.name}`}
                    title="Eliminar mes"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
                <div
                  className="p-2 cursor-pointer"
                  onClick={() => handleClick(month)}
                  aria-label={`Ver ${month.name}`}
                >
                  <ChevronRight />
                </div>
              </div>
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm cursor-pointer"
              onClick={() => handleClick(month)}
            >
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Ingresos:</span>
                <span className="font-semibold text-green-600 dark:text-green-400 text-right">
                  <MonthHistoryAmountColumn
                    month={month}
                    field="income"
                    formatAmount={formatAmount}
                    fallbackCurrency={defaultCurrency}
                  />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Ahorro:</span>
                <span className="font-semibold text-orange-600 dark:text-orange-400 text-right">
                  <MonthHistoryAmountColumn
                    month={month}
                    field="savings"
                    formatAmount={formatAmount}
                    fallbackCurrency={defaultCurrency}
                  />
                </span>
              </div>
              <div className="flex justify-between col-span-1 sm:col-span-2 pt-2">
                {month.pendientes === 0 ? (
                  <span className="text-green-600 dark:text-green-400 font-medium text-xs">Sin pendientes</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 flex items-center justify-center text-xs font-bold">
                      {month.pendientes}
                    </span>
                    {month.pendientes === 1 ? "Pendiente" : "Pendientes"}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

MonthlyHistoryCards.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      ingresos: PropTypes.number.isRequired,
      gastos: PropTypes.number.isRequired,
      ahorro: PropTypes.number.isRequired,
      restante: PropTypes.number.isRequired,
      pendientes: PropTypes.number.isRequired,
      summaryCurrency: PropTypes.string
    })
  ).isRequired,
  formatAmount: PropTypes.func.isRequired,
  onMonthClick: PropTypes.func,
  onDeleteMonth: PropTypes.func,
  defaultCurrency: PropTypes.string
};

export { MonthlyHistoryCards };
